import {
  type OfferTemplatePlanData,
  type OfferTemplateRes,
  PurchaseContext,
} from '@on3/api/src';
import { PriceDisplay } from '@on3/ui-lib/components/Subscription/PriceDisplay';
import { useSubscribe } from '@on3/ui-lib/components/Subscription/Provider';
import { TheAthletic } from '@on3/ui-lib/components/Subscription/Templates/Join/TheAthletic';
import {
  type AltPromoType,
  type PurchaseModalProps,
} from '@on3/ui-lib/types/purchase';

import styles from './Join.module.scss';

interface JoinProps {
  handleJoin: (props: PurchaseModalProps) => void;
  props?: Record<string, unknown>;
  joinType?: 'join' | 'winback' | 'intro' | 'gift' | AltPromoType;
  tmpVariant?: string;
  tmpTemplate?: OfferTemplateRes;
}

export type JoinTemplateProps = Omit<JoinProps, 'handleJoin'> & {
  handleJoin: (
    planKey: PurchaseModalProps['planKey'],
    couponKey?: PurchaseModalProps['couponKey'],
  ) => void;
  template: OfferTemplateRes;
};

interface JoinCardProps extends OfferTemplatePlanData {
  planKey?: string | null;
}

const DefaultJoin = ({ handleJoin, props, template }: JoinTemplateProps) => {
  const { subscribeText } = useSubscribe();

  const JoinCard = ({
    planKey,
    line1,
    line2,
    line3,
    line4,
    line5,
    buttonText,
  }: JoinCardProps) => (
    <div className={styles.block} data-template-type="join" {...props}>
      <div className={styles.header}>
        {line1 && <h2>{line1}</h2>}
        {line2 && <span className={styles.highlight}>{line2}</span>}
      </div>
      <p>{line3}</p>
      <div className={styles.pricing}>
        {line4 && <PriceDisplay info={line5} price={line4} />}
      </div>
      <button
        className={styles.joinBtn}
        onClick={() => handleJoin(planKey)}
        type="button"
      >
        {buttonText || subscribeText}
      </button>
    </div>
  );

  const cards = [
    {
      planKey: template.planKey1,
      planData: template.planData1,
    },
    {
      planKey: template.planKey2,
      planData: template.planData2,
    },
    {
      planKey: template.planKey3,
      planData: template.planData3,
    },
  ].filter((card) => !!card.planKey);

  return (
    <>
      {cards.map(({ planKey, planData }) => (
        <JoinCard
          buttonText={planData?.buttonText}
          key={planKey}
          line1={planData?.line1}
          line2={planData?.line2}
          line3={planData?.line3}
          line4={planData?.line4}
          line5={planData?.line5}
          planKey={planKey}
        />
      ))}
    </>
  );
};

export const Join = ({
  handleJoin,
  props,
  joinType = 'join',
  tmpVariant,
  tmpTemplate,
}: JoinProps) => {
  const {
    // @ts-expect-error TODO: add all types to api
    templates: { [joinType]: joinTemplate },
  } = useSubscribe();

  const template = tmpTemplate || joinTemplate;
  const variant = tmpVariant || template?.key || 'join';

  // TODO: recurly - if the user is subscribed to the site, we should check to see if we should show an upgrade
  // TODO: recurly - if the user is a premium user, we should show a thank you message instead
  if (!template) return null;

  const joinProps = {
    handleJoin: (
      planKey: PurchaseModalProps['planKey'],
      couponKey?: PurchaseModalProps['couponKey'],
    ) => {
      handleJoin({
        context:
          joinType === 'winback'
            ? PurchaseContext.Winback
            : PurchaseContext.Join,
        planKey,
        couponKey,
      });
    },
    template,
    props: props || {},
  };

  return (
    <>
      {variant === joinType && <DefaultJoin {...joinProps} />}
      {variant === `${joinType}-the-athletic` && <TheAthletic {...joinProps} />}
    </>
  );
};
